import { useEffect, useRef, useState } from "react";
import { Send, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { streamChat } from "@/lib/assistant";

function friendlyError(msg) {
  if (msg && /budget|balance/i.test(msg)) {
    return "The AI assistant's Universal Key balance is empty. Add balance in Profile → Universal Key to enable the Dap Advisor.";
  }
  return "The assistant is unavailable right now. Please try again.";
}

export default function AssistantChat({
  mode = "general",
  sessionId,
  dapId = null,
  welcome = "How can I help you keep an honorable agreement?",
  placeholder = "Ask the Dap Advisor…",
  quickActions = [],
  heightClass = "h-80",
  onLatestReply = null,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [model, setModel] = useState("claude");
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/assistant/history?session_id=${encodeURIComponent(sessionId)}`);
        const loaded = r.data.map((m) => ({ role: m.role, content: m.content }));
        // Don't clobber optimistic messages if the user already started chatting.
        setMessages((prev) => (prev.length === 0 ? loaded : prev));
      } catch (e) {
        console.error("Failed to load chat history:", e);
      }
    })();
    // api & encodeURIComponent are module-level/global singletons; only sessionId varies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    const acc = { current: "" };
    await streamChat(
      { session_id: sessionId, message: msg, mode, dap_id: dapId, model },
      {
        onDelta: (d) => {
          acc.current += d;
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (!last || last.role !== "assistant") {
              copy.push({ role: "assistant", content: d });
            } else {
              copy[copy.length - 1] = { role: "assistant", content: last.content + d };
            }
            return copy;
          });
        },
        onError: (err) =>
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last && last.role === "assistant" && !last.content) {
              copy[copy.length - 1] = { role: "assistant", content: friendlyError(err) };
            } else {
              copy.push({ role: "assistant", content: friendlyError(err) });
            }
            return copy;
          }),
        onDone: () => {
          setBusy(false);
          if (onLatestReply && acc.current.trim()) onLatestReply(acc.current.trim());
        },
      }
    );
  };

  return (
    <div className="flex flex-col" data-testid={`assistant-chat-${mode}`}>
      <div className="flex items-center justify-end gap-1 mb-2">
        <span className="text-[10px] uppercase tracking-wider text-[#A3A095] mr-1">Model</span>
        {[["claude", "Claude"], ["gemini", "Gemini"]].map(([val, label]) => (
          <button
            key={val}
            data-testid={`model-switch-${val}`}
            onClick={() => setModel(val)}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
              model === val
                ? "bg-[#1B4D3E] text-white border-[#1B4D3E]"
                : "border-[#DEDBD3] text-[#5C5A54] hover:bg-[#F0EBE1]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div
        ref={scrollRef}
        className={`${heightClass} overflow-y-auto space-y-3 pr-1`}
        data-testid="assistant-messages"
      >
        {messages.length === 0 && (
          <div className="flex items-start gap-2 text-[#5C5A54]">
            <Sparkles size={16} className="text-[#E07A5F] mt-1 shrink-0" />
            <p className="text-sm leading-relaxed">{welcome}</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={`assistant-message-${m.role}`}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#1B4D3E] text-white rounded-br-sm"
                  : "bg-[#F0EBE1] text-[#1A1A1A] rounded-bl-sm"
              }`}
            >
              {m.content || (busy && i === messages.length - 1 ? "…" : "")}
            </div>
          </div>
        ))}
      </div>

      {quickActions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {quickActions.map((qa, i) => (
            <button
              key={i}
              data-testid={`assistant-quick-${i}`}
              disabled={busy}
              onClick={() => send(typeof qa.message === "function" ? qa.message() : qa.message)}
              className="text-xs px-3 py-1.5 rounded-full border border-[#E07A5F]/40 text-[#B5541F] hover:bg-[#FBEEE6] disabled:opacity-50 transition-colors"
            >
              {qa.label}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <input
          data-testid="assistant-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={placeholder}
          disabled={busy}
          className="flex-1 bg-white border border-[#DEDBD3] rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B4D3E] focus:border-[#1B4D3E] transition-all"
        />
        <button
          data-testid="assistant-send-btn"
          onClick={() => send()}
          disabled={busy || !input.trim()}
          className="w-10 h-10 rounded-full bg-[#E07A5F] hover:bg-[#C96A51] disabled:opacity-50 text-white flex items-center justify-center shrink-0 transition-colors"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
